// src/components/Common/TrackingMapComponent.js
// Updated to use Mapbox with satellite imagery and caching
import React, { useState, useEffect } from 'react';
import UnifiedMapComponent from './UnifiedMapComponent';
import { FaMapMarkerAlt, FaFlag, FaTruck, FaUser, FaCheckCircle } from 'react-icons/fa';
import { useWebSocket } from '../../../../contexts/WebSocketContext';

const TrackingMapComponent = ({
  pickupLocation,
  deliveryLocation,
  riderLocation,
  route = [],
  orderStatus = 'pending',
  height = '500px',
  className = ''
}) => {
  const { socket } = useWebSocket();
  const [trackingData, setTrackingData] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]);

  // Update tracking data when props change
  useEffect(() => {
    const data = {
      pickup: pickupLocation ? {
        latitude: pickupLocation.lat || pickupLocation.latitude,
        longitude: pickupLocation.lng || pickupLocation.longitude,
        address: pickupLocation.address || pickupLocation.name || 'Pickup Location'
      } : null,
      delivery: deliveryLocation ? {
        latitude: deliveryLocation.lat || deliveryLocation.latitude,
        longitude: deliveryLocation.lng || deliveryLocation.longitude,
        address: deliveryLocation.address || deliveryLocation.name || 'Delivery Location'
      } : null,
      currentPosition: riderLocation ? {
        latitude: riderLocation.lat || riderLocation.latitude,
        longitude: riderLocation.lng || riderLocation.longitude,
        address: 'Current Position'
      } : null
    };

    setTrackingData(data);
  }, [pickupLocation, deliveryLocation, riderLocation]);

  // Update route coordinates
  useEffect(() => {
    if (route && route.length > 0) {
      // Convert route to [lng, lat] format for Mapbox
      const coords = route.map(point => [
        point.lng || point.longitude,
        point.lat || point.latitude
      ]);
      setRouteCoordinates(coords);
    }
  }, [route]);

  // Listen for real-time location updates via WebSocket
  useEffect(() => {
    if (socket) {
      const handleLocationUpdate = (data) => {
        if (data.type === 'location_update' && data.rider_location) {
          setTrackingData(prev => ({
            ...prev,
            currentPosition: {
              latitude: data.rider_location.lat,
              longitude: data.rider_location.lng,
              address: 'Current Position'
            }
          }));
        }
      };

      socket.on('location_update', handleLocationUpdate);
      
      return () => {
        socket.off('location_update', handleLocationUpdate);
      };
    }
  }, [socket]);

  // Determine map center based on available data
  const getMapCenter = () => {
    if (riderLocation) {
      return [
        riderLocation.lng || riderLocation.longitude,
        riderLocation.lat || riderLocation.latitude
      ];
    }
    if (pickupLocation) {
      return [
        pickupLocation.lng || pickupLocation.longitude,
        pickupLocation.lat || pickupLocation.latitude
      ];
    }
    return null; // Will use default center
  };

  // Create status indicator
  const getStatusInfo = () => {
    const statusMap = {
      'pending': { color: '#f59e0b', text: 'Order Pending' },
      'confirmed': { color: '#3b82f6', text: 'Order Confirmed' },
      'picked_up': { color: '#8b5cf6', text: 'Package Picked Up' },
      'in_transit': { color: '#06b6d4', text: 'In Transit' },
      'delivered': { color: '#10b981', text: 'Delivered' },
      'cancelled': { color: '#ef4444', text: 'Cancelled' }
    };
    
    return statusMap[orderStatus] || statusMap['pending'];
  };

  return (
    <div className={`relative ${className}`}>
      <UnifiedMapComponent
        trackingData={trackingData}
        showRoute={routeCoordinates.length > 0}
        routeCoordinates={routeCoordinates}
        height={height}
        center={getMapCenter()}
        zoom={14}
        initialStyle="satellite-streets"
        showControls={true}
        showStyleSwitcher={false}
        showLegend={true}
        showGeofence={false}
        showLandmarks={false}
        showBufferZone={false}
      />
      
      {/* Status Overlay */}
      <div className="absolute top-4 left-4 bg-white border border-gray-300 rounded-lg p-3 shadow-sm">
        <div className="flex items-center space-x-2">
          <div 
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: getStatusInfo().color }}
          ></div>
          <span className="text-sm font-medium">{getStatusInfo().text}</span>
        </div>
        
        {trackingData && (
          <div className="mt-2 text-xs text-gray-600">
            {trackingData.pickup && (
              <div>📍 Pickup: {trackingData.pickup.address}</div>
            )}
            {trackingData.delivery && (
              <div>🏁 Delivery: {trackingData.delivery.address}</div>
            )}
            {trackingData.currentPosition && (
              <div>🚚 Current: Moving to destination</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingMapComponent;